import React, { useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import ApiClient from "../utils/api";
import { 
  Users, Search, Copy, Check, ChevronRight, MessageSquare, 
  Calendar, Clock, Database, Tag, ShieldAlert, AlertCircle, 
  Mail, Phone, User, RefreshCw, Filter
} from "lucide-react";

export default function Contacts({ activeAgentId }) {
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selection & Search States
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Chat History Drawer/Accordion States
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionHistories, setSessionHistories] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const agentId = activeAgentId || ApiClient.currentAgentId;

  // Load Contacts on Mount/Agent Change
  useEffect(() => {
    setSelectedContact(null);
    setActiveSessionId(null);
    setSessionHistories({});
    setSearchTerm("");
    setDebouncedSearch("");
    setCurrentPage(1);
  }, [agentId]);

  // Load Contacts when page or search query changes
  useEffect(() => {
    fetchContacts(currentPage, debouncedSearch);
  }, [agentId, currentPage, debouncedSearch]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchContacts = async (page = currentPage, search = debouncedSearch) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.getContacts(page, itemsPerPage, search);
      const data = response?.contacts || [];
      const total = response?.total_count || 0;
      setContacts(data);
      setTotalContacts(total);
      
      // Retain previously selected contact if still present in the list
      let newSelected = null;
      if (selectedContact) {
        newSelected = data.find(c => c.userid === selectedContact.userid) || data[0] || null;
      } else if (data && data.length > 0) {
        newSelected = data[0];
      }
      setSelectedContact(newSelected);
      
      const hasActiveSession = newSelected && newSelected.sessions && newSelected.sessions.some(s => s.session_id === activeSessionId);
      if (activeSessionId && hasActiveSession) {
        setLoadingHistory(true);
        try {
          const history = await ApiClient.getWidgetHistory(activeSessionId, agentId);
          setSessionHistories({
            [activeSessionId]: history
          });
        } catch (err) {
          console.error("Error refreshing active session history:", err);
        } finally {
          setLoadingHistory(false);
        }
      } else {
        setActiveSessionId(null);
        setSessionHistories({});
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError("Failed to load contacts. Please verify backend status.");
    } finally {
      setLoading(false);
    }
  };

  // Load Session Chat History
  const handleToggleSession = async (sessionId) => {
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
      return;
    }

    setActiveSessionId(sessionId);

    // Only load if not already in cache
    if (!sessionHistories[sessionId]) {
      setLoadingHistory(true);
      try {
        const history = await ApiClient.getWidgetHistory(sessionId, agentId);
        setSessionHistories(prev => ({
          ...prev,
          [sessionId]: history
        }));
      } catch (err) {
        console.error("Error loading chat history:", err);
      } finally {
        setLoadingHistory(false);
      }
    }
  };

  // Clipboard copy helper
  const handleCopy = (text, field) => {
    if (!text || text === "N/A") return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getRandomColorClass = (name) => {
    const colors = [
      "bg-blue-100 text-blue-800 border-blue-200",
      "bg-purple-100 text-purple-800 border-purple-200",
      "bg-pink-100 text-pink-800 border-pink-200",
      "bg-indigo-100 text-indigo-800 border-indigo-200",
      "bg-teal-100 text-teal-800 border-teal-200",
      "bg-amber-100 text-amber-800 border-amber-200"
    ];
    let sum = 0;
    for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
    return colors[sum % colors.length];
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      return String(isoString);
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-4 lg:pt-6 lg:pb-12 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <PageHeader 
          title="Contacts Directory"
          description="Manage customer agents, inspect custom parameter metadata, and view historical conversation logs."
          actions={
            <button
              onClick={() => fetchContacts(currentPage, debouncedSearch)}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl font-bold text-sm uppercase shadow-sm transition-all duration-200"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh Data
            </button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[600px]">
          
          {/* Left Column: List Pane */}
          <div className="lg:col-span-4 bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col h-[750px] overflow-hidden">
            
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3.5 top-3.5 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search name, phone, email..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#83bbe1]/30 focus:border-[#83bbe1] transition-all"
                />
              </div>
            </div>

            {/* Contacts Scrollable List */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50 p-2">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2">
                  <RefreshCw size={24} className="animate-spin text-gray-400" />
                  <span className="text-xs font-medium">Analyzing records...</span>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center p-6 text-center text-red-500 gap-2">
                  <AlertCircle size={24} />
                  <span className="text-xs font-semibold">{error}</span>
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 gap-2">
                  <Users size={24} className="opacity-40" />
                  <span className="text-xs font-medium">No contacts match filter</span>
                </div>
              ) : (
                contacts.map(contact => (
                    <div
                      key={contact.id}
                      onClick={() => {
                        setSelectedContact(contact);
                        setActiveSessionId(null);
                      }}
                      className={`flex items-center gap-3 p-3 rounded-2xl cursor-pointer transition-all duration-150 ${
                        selectedContact?.id === contact.id
                          ? "bg-[#83bbe1]/10"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      {/* Avatar Initials */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs border ${getRandomColorClass(contact.name)}`}>
                        {getInitials(contact.name)}
                      </div>
                      
                      {/* Contact Basic Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-900 text-sm truncate">
                            {contact.name}
                          </h4>
                          <span className="text-[10px] font-bold text-gray-400">
                            {contact.sessions.length} {contact.sessions.length === 1 ? 'convo' : 'convos'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {contact.email !== 'N/A' ? contact.email : (contact.phone !== 'N/A' ? contact.phone : `ID: ${contact.userid}`)}
                        </p>
                        <div className="flex items-center gap-1 mt-1 text-[10px] font-medium text-gray-400">
                          <Clock size={10} />
                          <span>Active {formatDate(contact.last_active)}</span>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Pagination Controls */}
            {totalContacts > itemsPerPage && (
              <div className="p-4 border-t border-gray-100 flex items-center justify-between text-xs bg-gray-50/50 rounded-b-3xl">
                <div className="text-gray-500 font-medium">
                  Page {currentPage} of {Math.ceil(totalContacts / itemsPerPage)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 text-[10px] font-bold uppercase tracking-wider transition-all duration-150 shadow-sm"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage >= Math.ceil(totalContacts / itemsPerPage)}
                    className="px-3 py-1.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 text-[10px] font-bold uppercase tracking-wider transition-all duration-150 shadow-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Right Column: Detail Pane */}
          <div className="lg:col-span-8 bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col h-[750px] overflow-hidden">
            {selectedContact ? (
              <div className="flex flex-col h-full overflow-y-auto p-6 space-y-6">

                {/* Identity Cards & Parameters Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Contact Identity Metadata Card */}
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 space-y-4">
                    <h3 className="text-xs font-black text-gray-400 tracking-wider uppercase flex items-center gap-1.5">
                      <User size={14} className="text-[#83bbe1]" />
                      Identity Attributes
                    </h3>
                    
                    <div className="space-y-2">
                      {[
                        { label: "User ID", value: selectedContact.userid, icon: User },
                        { label: "Email Address", value: selectedContact.email, icon: Mail },
                        { label: "Phone Number", value: selectedContact.phone, icon: Phone }
                      ].map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-2 bg-white rounded-xl border border-gray-100">
                          <div className="flex items-center gap-2">
                            <item.icon size={13} className="text-gray-400" />
                            <span className="text-gray-500 font-medium">{item.label}</span>
                          </div>
                          <div className="flex items-center gap-1.5 max-w-[60%]">
                            <span className="font-bold text-gray-800 truncate" title={item.value}>
                              {item.value}
                            </span>
                            {item.value && item.value !== "N/A" && (
                              <button 
                                onClick={() => handleCopy(item.value, item.label)} 
                                className="text-gray-400 hover:text-gray-600 transition"
                              >
                                {copiedField === item.label ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Parameters Card */}
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xs font-black text-gray-400 tracking-wider uppercase flex items-center gap-1.5 mb-3">
                        <Tag size={14} className="text-[#83bbe1]" />
                        Custom Field Parameters
                      </h3>
                      
                      {Object.keys(selectedContact.custom_parameters || {}).filter(key => !key.toLowerCase().startsWith("eyecatcher") && !key.toLowerCase().includes("tenant")).length > 0 ? (
                        <div className="flex flex-wrap gap-2 max-h-[140px] overflow-y-auto">
                          {Object.entries(selectedContact.custom_parameters)
                            .filter(([key]) => !key.toLowerCase().startsWith("eyecatcher") && !key.toLowerCase().includes("tenant"))
                            .map(([key, val]) => (
                              val !== null && val !== undefined && (
                                <div key={key} className="flex items-center bg-white border border-gray-100 rounded-xl px-3 py-1.5 text-xs shadow-sm">
                                  <span className="font-black text-gray-500 mr-1.5 uppercase tracking-wide text-[10px]">
                                    {key}:
                                  </span>
                                  <span className="font-bold text-gray-800 truncate max-w-[120px]" title={val.toString()}>
                                    {val.toString()}
                                  </span>
                                </div>
                              )
                            ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 text-gray-400 gap-1.5">
                          <Database size={20} className="opacity-30" />
                          <span className="text-xs font-medium">No custom parameter bindings</span>
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {/* Conversation Session History */}
                <div className="space-y-3">
                  <h3 className="text-xs font-black text-gray-400 tracking-wider uppercase flex items-center gap-1.5">
                    <MessageSquare size={14} className="text-[#83bbe1]" />
                    Conversations Timeline
                  </h3>
                  
                  <div className="space-y-4">
                    {selectedContact.sessions.map((session, index) => {
                      const isOpen = activeSessionId === session.session_id;
                      const hasSummary = session.summary && (session.summary.summary || session.summary.topics);
                      
                      return (
                        <div 
                          key={session.session_id} 
                          className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm transition-all"
                        >
                          {/* Timeline Header Row */}
                          <div 
                            onClick={() => handleToggleSession(session.session_id)}
                            className={`flex justify-between items-center p-4 cursor-pointer transition-colors ${
                              isOpen ? "bg-slate-50 border-b border-gray-100" : "hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-xl ${isOpen ? "bg-[#83bbe1] text-white" : "bg-blue-50 text-[#83bbe1]"}`}>
                                <MessageSquare size={16} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-extrabold text-gray-800 text-sm">
                                    Convo Session {selectedContact.sessions.length - index}
                                  </span>
                                  <span className="text-[10px] text-gray-400 font-mono">
                                    ({session.session_id.slice(0, 64)})
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Started: {formatDate(session.created_at)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <span className="text-xs text-gray-400 font-medium">
                                Active: {formatDate(session.updated_at)}
                              </span>
                              <ChevronRight 
                                size={16} 
                                className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} 
                              />
                            </div>
                          </div>

                          {/* Expanded Transcript Section */}
                          {isOpen && (
                            <div className="bg-white p-4 space-y-4">
                              
                              {/* Session Summary if Generated */}
                              {hasSummary && (
                                <div className="bg-[#83bbe1]/5 border border-[#83bbe1]/20 rounded-xl p-3.5 text-xs text-gray-700 space-y-2">
                                  <div className="flex items-center gap-1.5">
                                    <SparklesIcon className="text-[#83bbe1] w-3.5 h-3.5" />
                                    <span className="font-bold text-gray-900">AI Conversation Summary</span>
                                  </div>
                                  {session.summary.summary && (
                                    <p className="leading-relaxed font-medium">{session.summary.summary}</p>
                                  )}
                                  {session.summary.topics && session.summary.topics.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {session.summary.topics.map((topic, tid) => (
                                        <span key={tid} className="bg-white border border-gray-200 px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-500">
                                          #{topic}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Transcript Logs */}
                              <div className="border border-gray-100 rounded-2xl overflow-hidden">
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 flex justify-between items-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
                                  <span>Transcript Logs</span>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setLoadingHistory(true);
                                      try {
                                        const history = await ApiClient.getWidgetHistory(session.session_id, agentId);
                                        setSessionHistories(prev => ({
                                          ...prev,
                                          [session.session_id]: history
                                        }));
                                      } catch (err) {
                                        console.error("Error refreshing session history:", err);
                                      } finally {
                                        setLoadingHistory(false);
                                      }
                                    }}
                                    className="flex items-center gap-1 text-[#83bbe1] hover:text-[#5fa2cc] transition-colors font-bold uppercase"
                                    disabled={loadingHistory}
                                  >
                                    <RefreshCw size={10} className={loadingHistory ? "animate-spin" : ""} />
                                    Refresh Session
                                  </button>
                                </div>
                                
                                <div className="p-4 max-h-[300px] overflow-y-auto space-y-3 bg-gray-50/30">
                                  {loadingHistory ? (
                                    <div className="flex justify-center items-center py-8 text-gray-400 gap-2">
                                      <RefreshCw size={14} className="animate-spin text-[#83bbe1]" />
                                      <span className="text-xs font-semibold">Decrypting logs...</span>
                                    </div>
                                  ) : !sessionHistories[session.session_id] || sessionHistories[session.session_id].length === 0 ? (
                                    <div className="text-center text-xs text-gray-400 py-6">
                                      No conversational logs found for this session.
                                    </div>
                                  ) : (
                                    sessionHistories[session.session_id].map((msg, mid) => {
                                      const isUser = msg.type === "user";
                                      const isHumanAgent = msg.type === "human_agent";
                                      
                                      return (
                                        <div 
                                          key={mid} 
                                          className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                                        >
                                          <div className={`max-w-[75%] rounded-2xl p-3 shadow-sm border break-words ${
                                            isUser 
                                              ? "bg-[#4c3850] text-white border-[#4c3850]" 
                                              : isHumanAgent
                                                ? "bg-amber-50 text-amber-900 border-amber-200"
                                                : "bg-white text-gray-800 border-gray-100"
                                          }`}>
                                            <div className="flex justify-between items-center mb-1 text-[9px] font-extrabold uppercase tracking-wide opacity-75">
                                              <span>{isUser ? "Customer" : isHumanAgent ? "Human Agent" : "AI Copilot"}</span>
                                            </div>
                                            <p className="text-xs leading-relaxed font-medium whitespace-pre-wrap">
                                              {msg.message}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </div>

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400 gap-3">
                <Users size={48} className="opacity-20 stroke-[1.5]" />
                <div className="space-y-1">
                  <h3 className="font-bold text-gray-700 text-sm">No Contact Selected</h3>
                  <p className="text-xs text-gray-400 max-w-sm">
                    Select a contact card from the left side panel to inspect customer agents, parameters, and dynamic transcripts.
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}

// Sparkles local SVG helper
function SparklesIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z" />
      <path d="m5 3 1 2.5L8.5 6 6 7 5 9.5 4 7 1.5 6 4 5.5Z" />
      <path d="m19 17 1 2.5 2.5.5-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1Z" />
    </svg>
  );
}
